import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, AnswerExplainer, QuestionDiagram, StepsList } from '../../shared/components';
import { api } from '../../utils/api';
import { noCopyHandlers } from '../../utils/noCopy';
import './CollegeAlgebra.css';

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function UnitTestRunner({ unitSlug, onExit }) {
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(null);
  const submittedRef = useRef(false);

  useEffect(() => {
    api
      .post(`/api/college-algebra/units/${unitSlug}/test/start`, {})
      .then((data) => {
        setTest(data);
        setSecondsLeft(data.durationMinutes * 60);
      })
      .catch(() => setError('Unable to start the unit test right now.'));
  }, [unitSlug]);

  useEffect(() => {
    if (secondsLeft === null || result) return undefined;
    if (secondsLeft <= 0) {
      handleSubmit();
      return undefined;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft, result]);

  function selectAnswer(questionId, selectedIndex) {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedIndex }));
  }

  async function handleSubmit() {
    if (submittedRef.current || !test) return;
    submittedRef.current = true;

    const payload = test.questions.map((q) => ({ questionId: q.id, selectedIndex: answers[q.id] }));
    try {
      const response = await api.post(`/api/college-algebra/test/${test.testAttemptId}/submit`, { answers: payload });
      setResult(response);
    } catch (err) {
      setError('Unable to submit your test right now.');
    }
  }

  if (error) return <p className="ca-error">{error}</p>;
  if (!test) return <p>Preparing your test&hellip;</p>;

  if (result) {
    const resultsById = new Map(result.results.map((r) => [r.questionId, r]));
    return (
      <div className="ca-page">
        <h1>{test.unit.name} &mdash; Test Results</h1>
        <Card className="ca-test-result-card">
          <p className="ca-test-result-score">
            {result.correctCount} / {result.totalQuestions} ({Math.round((result.correctCount / result.totalQuestions) * 100)}%)
          </p>
          <p className={`ca-test-result-status ca-test-result-status--${result.passed ? 'passed' : 'failed'}`}>
            {result.passed ? 'Passed' : 'Not yet passing'} &mdash; 70% or higher passes
          </p>
        </Card>

        <h2 className="ca-lessons-heading">Concept breakdown</h2>
        {Object.entries(result.sectionBreakdown).map(([skill, section]) => (
          <Card key={skill} className="ca-breakdown-card">
            <span>{skill.replace(/-/g, ' ')}</span>
            <span>{section.correct} / {section.total}</span>
          </Card>
        ))}

        <h2 className="ca-lessons-heading" style={{ marginTop: 20 }}>Question review</h2>
        {test.questions.map((q, index) => {
          const r = resultsById.get(q.id);
          return (
            <Card
              key={q.id}
              className={`quiz-question-review quiz-no-copy ${r.correct ? 'quiz-question-review--correct' : 'quiz-question-review--incorrect'}`}
              {...noCopyHandlers}
            >
              <p className="quiz-question-text">{index + 1}. {q.question_text}</p>
              <QuestionDiagram svg={q.image_svg} />
              <p className="quiz-question-answer">Correct answer: {q.choices[r.correctIndex]}</p>
              <StepsList steps={r.steps} />
              {r.explanation && <p className="quiz-question-explanation">{r.explanation}</p>}
              {!r.correct && (
                <AnswerExplainer
                  explanation={r.aiExplanation}
                  questionText={q.question_text}
                  choices={q.choices}
                  selectedIndex={answers[q.id]}
                  correctIndex={r.correctIndex}
                />
              )}
            </Card>
          );
        })}

        <Button onClick={onExit}>Back to unit</Button>
      </div>
    );
  }

  const allAnswered = test.questions.every((q) => answers[q.id] !== undefined);

  return (
    <div className="ca-page">
      <div className="ca-test-header">
        <h1>{test.unit.name} &mdash; Unit Test</h1>
        <span className="ca-test-timer">{formatTime(secondsLeft)}</span>
      </div>

      {test.questions.map((q, index) => (
        <Card key={q.id} className="quiz-question-card quiz-no-copy" {...noCopyHandlers}>
          <p className="quiz-question-text">{index + 1}. {q.question_text}</p>
          <QuestionDiagram svg={q.image_svg} />
          <div className="quiz-choices">
            {q.choices.map((choice, i) => (
              <label key={i} className="quiz-choice">
                <input
                  type="radio"
                  name={`test-question-${q.id}`}
                  checked={answers[q.id] === i}
                  onChange={() => selectAnswer(q.id, i)}
                />
                {choice}
              </label>
            ))}
          </div>
        </Card>
      ))}

      <div className="quizzes-actions">
        <Button variant="secondary" onClick={onExit}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!allAnswered}>Submit test</Button>
      </div>
    </div>
  );
}
